import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  Image,
  Tag,
  HStack,
  useColorModeValue,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

const BlogPost = ({ title, date, description, image, tags, readTime, isNew }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  
  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      bg={cardBg}
      borderRadius="xl"
      overflow="hidden"
      shadow="lg"
      position="relative"
      sx={{
        transition: 'all 0.3s',
        _hover: {
          transform: 'translateY(-5px)',
          boxShadow: '2xl',
        }
      }}
    >
      {isNew && (
        <Badge
          position="absolute"
          top={4}
          right={4}
          colorScheme="brand"
          fontSize="sm"
          borderRadius="full"
          px={3}
          py={1}
          zIndex={1}
        >
          New
        </Badge>
      )}
      <Box position="relative" h="200px">
        <Image
          src={image}
          alt={title}
          w="full"
          h="full"
          objectFit="cover"
          transition="transform 0.3s"
          _hover={{ transform: 'scale(1.05)' }}
        />
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          h="100px"
          bgGradient="linear(to-t, blackAlpha.600, transparent)"
        />
      </Box>
      <VStack p={6} align="start" spacing={4}>
        <HStack spacing={2} flexWrap="wrap">
          {tags.map((tag) => (
            <Tag 
              key={tag} 
              colorScheme="brand" 
              size="sm"
              borderRadius="full"
              px={3}
              py={1}
              mb={2}
            >
              {tag}
            </Tag>
          ))}
        </HStack>
        <Heading 
          size="md" 
          lineHeight="tall"
          _hover={{ color: 'brand.500' }}
          transition="color 0.2s"
        >
          {title}
        </Heading>
        <Text 
          color={useColorModeValue('gray.600', 'gray.300')} 
          noOfLines={3}
          fontSize="md"
          lineHeight="tall"
        >
          {description}
        </Text>
        <HStack 
          justify="space-between" 
          w="full" 
          color={useColorModeValue('gray.600', 'gray.400')}
          fontSize="sm"
          pt={2}
        >
          <Text fontWeight="medium">{date}</Text>
          <HStack spacing={1}>
            <Text>●</Text>
            <Text>{readTime} min read</Text>
          </HStack>
        </HStack>
      </VStack>
    </MotionBox>
  );
};

const Blog = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  const blogPosts = [
    {
      title: "SEBI's New Mutual Fund Expense Ratio Guidelines: What Investors Need to Know",
      date: "March 15, 2024",
      description: "SEBI has implemented new expense ratio calculations for mutual funds, potentially reducing costs for investors. The regulatory body has set a cap on Total Expense Ratio (TER) based on asset size and category.",
      image: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      tags: ["Regulation", "SEBI Update"],
      readTime: 5,
      isNew: true
    },
    {
      title: "Small Cap Funds Under Pressure: AMCs Implement Restrictions",
      date: "March 14, 2024",
      description: "Leading fund houses including SBI MF, Nippon India, and Kotak have put restrictions on small-cap fund investments amid valuation concerns. What this means for retail investors.",
      image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      tags: ["Market Trends", "Small Cap"],
      readTime: 4,
      isNew: true
    },
    {
      title: "Debt Mutual Funds See Surge in Inflows Post Tax Changes",
      date: "March 12, 2024",
      description: "Debt mutual funds witness significant inflows following the implementation of new tax rules. Corporate bond funds and banking PSU funds lead the recovery.",
      image: "https://images.unsplash.com/photo-1579532537598-459ecdaf39cc?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      tags: ["Debt Funds", "Tax Impact"],
      readTime: 6
    },
    {
      title: "SIP Investments Hit New Record: ₹18,000 Crore Monthly Inflow",
      date: "March 10, 2024",
      description: "Systematic Investment Plans (SIPs) continue to gain popularity among retail investors, reaching a historic high in monthly contributions.",
      image: "https://images.unsplash.com/photo-1559526324-593bc073d938?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      tags: ["SIP", "Investment Trends"],
      readTime: 3
    },
    {
      title: "International Fund of Funds: SEBI Eases Investment Norms",
      date: "March 8, 2024",
      description: "Regulatory changes allow greater flexibility for international mutual funds, opening new opportunities for geographical diversification.",
      image: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      tags: ["International", "Regulation"],
      readTime: 5
    },
    {
      title: "ESG Funds Gain Traction: Sustainable Investing on the Rise",
      date: "March 6, 2024",
      description: "Environmental, Social, and Governance (ESG) focused mutual funds see increased investor interest as sustainability becomes a key investment criterion.",
      image: "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      tags: ["ESG", "Sustainable Investing"],
      readTime: 4
    }
  ];

  return (
    <Box bg={bgColor} minH="100vh" py={16}>
      <Container maxW="container.xl">
        <VStack spacing={16}>
          {/* Header Section */}
          <VStack spacing={6} textAlign="center" w="full">
            <Badge
              colorScheme="brand"
              px={4}
              py={2}
              borderRadius="full"
              fontSize="md"
            >
              Latest Updates
            </Badge>
            <Heading 
              size="2xl" 
              bgGradient="linear(to-r, brand.500, brand.600)"
              bgClip="text"
            >
              Market Insights
            </Heading>
            <Text 
              fontSize="xl" 
              color={useColorModeValue('gray.600', 'gray.300')}
              maxW="container.md"
              lineHeight="tall"
            >
              Stay updated with the latest trends and developments in the Indian mutual fund industry
            </Text>
            <Divider width="100px" borderWidth="2px" borderColor="brand.500" />
          </VStack>

          {/* Blog Grid */}
          <SimpleGrid 
            columns={{ base: 1, md: 2, lg: 3 }} 
            spacing={8}
            w="full"
          >
            {blogPosts.map((post, index) => (
              <BlogPost key={index} {...post} />
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

export default Blog;
